import { reportError } from '@/utils/error-reporting'
import AttachmentController from '@controllers/AttachmentController'
import { router } from '@inertiajs/vue3'
import axios from 'axios'
import { useToast } from 'primevue/usetoast'
import { ref, type Ref } from 'vue'
import { useI18n } from 'vue-i18n'

interface UploadTarget {
  attachableType: string
  attachableId: string
  documentType?: string | null
}

interface DocumentUpload {
  uploading: Ref<boolean>
  upload: (target: UploadTarget, files: File[]) => Promise<void>
  classify: (attachmentId: string, documentType: string) => void
  remove: (attachmentId: string) => void
}

export function useDocumentUpload(): DocumentUpload {
  const { t } = useI18n({ useScope: 'global' })
  const toast = useToast()
  const uploading = ref(false)

  const reportFailure = (error: unknown): void => {
    const status = axios.isAxiosError(error) ? error.response?.status : undefined

    toast.add({
      severity: 'error',
      summary: status === 413 ? t('documentUpload.tooLarge') : t('documentUpload.failed'),
      detail: axios.isAxiosError(error) ? error.response?.data?.message : undefined,
      life: 8000,
    })

    reportError('Document upload failed', error)
  }

  const upload = async (target: UploadTarget, files: File[]): Promise<void> => {
    if (!files.length) {
      return
    }

    const payload = new FormData()

    files.forEach((file) => payload.append('files[]', file))

    if (target.documentType) {
      payload.append('description', target.documentType)
    }

    uploading.value = true

    try {
      await axios.post(
        AttachmentController.upload({
          type: 'document',
          attachableType: target.attachableType,
          attachableId: target.attachableId,
        }).url,
        payload,
      )

      router.reload()

      toast.add({ severity: 'success', summary: t('documentUpload.uploaded', files.length), life: 5000 })
    } catch (error) {
      reportFailure(error)
    } finally {
      uploading.value = false
    }
  }

  const classify = (attachmentId: string, documentType: string): void => {
    router.patch(
      AttachmentController.update(attachmentId).url,
      { description: documentType },
      { preserveScroll: true, preserveState: true },
    )
  }

  const remove = (attachmentId: string): void => {
    router.delete(AttachmentController.destroy(attachmentId).url, {
      preserveScroll: true,
      preserveState: true,
    })
  }

  return { uploading, upload, classify, remove }
}
